import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { Autocomplete, Box, Tooltip, createFilterOptions } from "@mui/material";
import { useDispatch } from "react-redux";
import { AppDispatch } from "index";
import { fetchOrganisations } from "slices/UserManagement/organisation/thunk";
import { SortingState } from "@tanstack/react-table";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { autocompleteDropdownStyles } from "Constants/Style";
import { MecordColors } from "Constants/Color";
interface BillingFromAddressProps {
  value: any; // Can be string, object, or null
  onChange: (value: any) => void;
  onBlur?: () => void;
  error?: string;
  disabled?: boolean;
}

const filterOrganizationOptions = createFilterOptions<any>({
  stringify: (option) => {
    if (typeof option === "string") return option;
    return [
      option?.organization_name,
      option?.org_address,
      option?.org_city,
      option?.org_state,
      option?.org_country,
      option?.org_pincode,
      option?.industry_type,
    ]
      .filter(Boolean)
      .join(" ");
  },
});

const BillingFromAddress: React.FC<BillingFromAddressProps> = ({
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [organizationsState, setOrganizationsState] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageIndex] = useState(0);
  const [pageSize] = useState(100);
  const [sorting] = useState<SortingState>([]);
  const [filterValue] = useState<any[]>([]);
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  const getOptionText = useCallback((option: any): string => {
    if (typeof option === "string") return option;
    const parts = [];
    if (option?.organization_name) parts.push(option.organization_name);
    if (option?.org_address) parts.push(option.org_address);
    if (option?.org_city) parts.push(option.org_city);
    if (option?.org_country) parts.push(option.org_country);
    if (option?.org_pincode) parts.push(option.org_pincode);
    return parts.length > 0 ? parts.join(", ") : "";
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(inputValue.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [inputValue]);

  // Fetch organizations when search changes
  useEffect(() => {
    const handleFetchOrganization = async () => {
      try {
        setLoading(true);
        const resData = await dispatch(
          fetchOrganisations({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search: debouncedSearch,
            filters: filterValue,
          })
        ).unwrap();
        setOrganizationsState(resData?.data || []);
      } catch (err) {
        console.error("Failed to load organizations:", err);
      } finally {
        setLoading(false);
      }
    };

    handleFetchOrganization();
  }, [dispatch, pageIndex, pageSize, sortField, sortDirection, debouncedSearch, filterValue]);

  // Helper function to format full address with city, country, and pincode
  const formatFullAddress = (addr: any): string => {
    // console.log("addr", addr);
    if (!addr) return "";
    
    // If it's a string, return it as is
    if (typeof addr === "string") {
      return addr;
    }
    
    // If it's an empty object, return empty string
    if (
      typeof addr === "object" &&
      !Array.isArray(addr) &&
      Object.keys(addr).length === 0
    ) {
      return "";
    }
    
    // If it's an object with properties, construct full address
    if (
      typeof addr === "object" &&
      !Array.isArray(addr) &&
      Object.keys(addr).length > 0
    ) {
      const parts = [];
      // Use org_address if available, otherwise construct from fields
      if ((addr as any).org_address) {
        parts.push((addr as any).org_address);
      } else if ((addr as any).address) {
        parts.push((addr as any).address);
      }
      if ((addr as any).org_city) parts.push((addr as any).org_city);
      if ((addr as any).org_country) parts.push((addr as any).country);
      if ((addr as any).org_pin_code || (addr as any).org_pincode) {
        parts.push((addr as any).org_pincode || (addr as any).pin_code || (addr as any).pincode);
      }
      
      return parts.length > 0 ? parts.join(", ") : "";
    }
    
    return "";
  };

  // Get the actual value object for Autocomplete (to match selected option)
  const getAutocompleteValue = () => {
    if (!value) return null;
    
    // If value is a string, return it as is
    if (typeof value === "string") {
      return value;
    }
    
    // If value is an object, try to find matching option in organizationsState
    if (typeof value === "object" && !Array.isArray(value)) {
      // First, try to find exact match by ID
      const matchById = organizationsState.find(
        (org) =>
          (org?.organization_id &&
            ((value as any)?.organization_id || (value as any)?.id) &&
            org.organization_id ===
              ((value as any).organization_id || (value as any).id)) ||
          (org?.id &&
            ((value as any)?.id || (value as any)?.organization_id) &&
            org.id === ((value as any).id || (value as any).organization_id))
      );
      if (matchById) return matchById;
      
      // Then try to find by org_address
      const matchByAddress = organizationsState.find(
        (org) => org.org_address === value.org_address || org.org_address === value.address
      );
      if (matchByAddress) return matchByAddress;
      
      // If no match found, return the value object as is (for freeSolo)
      return value;
    }
    
    return null;
  };

  const autocompleteValue = getAutocompleteValue();

  const organizationOptions = useMemo(() => {
    const options = [...organizationsState];

    if (
      autocompleteValue &&
      typeof autocompleteValue === "object" &&
      !Array.isArray(autocompleteValue)
    ) {
      const selectedId =
        autocompleteValue.organization_id ?? autocompleteValue.id ?? null;
      const alreadyIncluded = options.some((org) => {
        const orgId = org?.organization_id ?? org?.id ?? null;
        return selectedId && orgId && orgId === selectedId;
      });

      if (!alreadyIncluded) {
        options.unshift(autocompleteValue);
      }
    }

    return options;
  }, [organizationsState, autocompleteValue]);

  useEffect(() => {
    if (!value) {
      setInputValue("");
      return;
    }

    setInputValue(getOptionText(value) || formatFullAddress(value));
  }, [value, getOptionText]);

  const handleChange = (_: any, newValue: any) => {
    // freeSolo Autocomplete can emit the label string on blur — resolve back to org object
    if (typeof newValue === "string") {
      const trimmedValue = newValue.trim();
      if (!trimmedValue) {
        onChange(null);
        return;
      }

      const matchedOrg = organizationOptions.find((org) => {
        const optionText = getOptionText(org);
        return (
          optionText === trimmedValue ||
          org?.organization_name === trimmedValue ||
          org?.org_address === trimmedValue
        );
      });

      if (matchedOrg) {
        onChange(matchedOrg);
        return;
      }

      // Keep existing object value if the freeSolo label still represents it
      if (
        value &&
        typeof value === "object" &&
        getOptionText(value) === trimmedValue
      ) {
        onChange(value);
        return;
      }

      onChange(trimmedValue);
      return;
    }

    if (newValue && typeof newValue === "object") {
      // Prefer full organization objects (must have an address field)
      if (newValue.org_address || newValue.address || newValue.organization_name) {
        onChange(newValue);
      } else {
        onChange(null);
      }
      return;
    }

    onChange(null);
  };

  const fullAddressForTooltip = formatFullAddress(value);

  return (
    <Form.Group style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
      <Form.Label>Bill From<span className="text-danger">*</span></Form.Label>
      <Tooltip
        title={fullAddressForTooltip || "No address available"}
        arrow
        placement="top"
      >
        <div style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", overflow: "hidden" }}>
          <Autocomplete
            freeSolo
            disabled={disabled}
            loading={loading}
            options={organizationOptions}
            inputValue={inputValue}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === "input" || reason === "clear" || reason === "reset") {
                setInputValue(newInputValue);
              }
            }}
            getOptionLabel={(option) => {
              return getOptionText(option);
            }}
            filterOptions={filterOrganizationOptions}
            isOptionEqualToValue={(option, value) => {
              // Handle comparison for freeSolo Autocomplete
              if (typeof value === "string") {
                // If value is a string, compare with option's org_address
                if (typeof option === "string") {
                  return option === value;
                }
                const optionText = getOptionText(option);
                return !!optionText && optionText === value;
              }
              // If value is an object, compare IDs or addresses
              if (typeof option === "object" && typeof value === "object") {
                const optionOrgId =
                  (option as any).organization_id ?? (option as any).id ?? null;
                const valueOrgId =
                  (value as any).organization_id ?? (value as any).id ?? null;

                // Only compare if BOTH ids are truthy (avoid undefined === undefined matching everything)
                if (optionOrgId && valueOrgId) {
                  return optionOrgId === valueOrgId;
                }

                // If no valid IDs, do NOT try to match by address here (can cause multiple matches)
                return false;
              }
              return false;
            }}
            value={autocompleteValue}
            onChange={handleChange}
            onBlur={onBlur}
            ListboxProps={autocompleteDropdownStyles.ListboxProps}
            sx={{
              width: "100%",
              maxWidth: "100%",
              "& .MuiAutocomplete-root": {
                width: "100%",
                maxWidth: "100%",
              },
            }}
            renderOption={(props, option, { selected }) => {
              const { key, ...liProps } = props;
              // Format full address for dropdown options
              const optionText = getOptionText(option);

              const optionKey =
                typeof option === "string"
                  ? `billing-address-${option}`
                  : (option as any).organization_id ||
                    (option as any).id ||
                    `billing-address-${(option as any).org_address || ""}-${(option as any).org_city || ""}-${(option as any).org_pincode || ""}-${(option as any).sr_no || ""}`;
              
              return (
                <li {...liProps} key={optionKey} style={{ padding: 0 }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.8,
                      width: "100%",
                      maxWidth: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      "&:hover": {
                        backgroundColor: MecordColors.Hover,
                        color: MecordColors.ActiveText,
                      },
                      ...(selected && {
                        backgroundColor: MecordColors.ActiveBackground,
                        color: MecordColors.ActiveText,
                      }),
                    }}
                  >
                    <span style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>{optionText}</span>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Search or select billing address"
                size="small"
                error={!!error}
                helperText={error || ""}
                required
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  "& .MuiInputBase-input": {
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: disabled ? "not-allowed" : "text",
                    minWidth: 0, // Allow input to shrink below its default min-width
                    width: "100%",
                    maxWidth: "100%",
                  },
                  "& .MuiInputBase-root": {
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "13px",
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    overflow: "hidden",
                  },
                  "& .MuiAutocomplete-inputRoot": {
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    "& .MuiAutocomplete-input": {
                      minWidth: 0, // Allow input to shrink
                      width: "100% !important",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  },
                  "& .MuiAutocomplete-input": {
                    minWidth: 0, // Allow input to shrink
                    width: "100% !important",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            )}
          />
        </div>
      </Tooltip>
    </Form.Group>
  );
};

export default BillingFromAddress;

